import * as React from 'react'
import type {PreloadedQuery} from 'react-relay'
import {usePreloadedQuery, useQueryLoader} from 'react-relay'
import {createSelector} from 'reselect'

import type {AgentPool} from '../../../../../../services/rest'
import {restApi} from '../../../../../../services/rest'
import type {AgentPoolId} from '../../../../../../types'
import {stringifyId, toAgentPoolId} from '../../../../../../types'
import {emptyArray} from '../../../../../../utils/empty'
import type {
  AgentsPagesPoolsAuthorizePermissionsQuery,
  AgentsPagesPoolsAuthorizePermissionsQuery$data,
} from '../../../__generated__/AgentsPagesPoolsAuthorizePermissionsQuery.graphql'
import {DEFAULT_AGENT_POOL_ID} from '../../../AgentPoolPage/AgentPoolPage.constants'
import {poolsAuthorizePermissionsQuery} from '../../../AgentsPages.queries'

export function usePoolsAuthorizePermissionsQueryRef() {
  const [
    poolsAuthorizePermissionsQueryRef,
    loadPoolsAuthorizePermissionsQuery,
    disposePoolsAuthorizePermissionsQuery,
  ] = useQueryLoader<AgentsPagesPoolsAuthorizePermissionsQuery>(poolsAuthorizePermissionsQuery)

  React.useEffect(() => {
    loadPoolsAuthorizePermissionsQuery({}, {fetchPolicy: 'store-and-network'})

    return disposePoolsAuthorizePermissionsQuery
  }, [disposePoolsAuthorizePermissionsQuery, loadPoolsAuthorizePermissionsQuery])

  return [poolsAuthorizePermissionsQueryRef]
}

const reducePoolsAuthorizePermissionsResponse = createSelector(
  (response: AgentsPagesPoolsAuthorizePermissionsQuery$data) => response,
  (_: AgentsPagesPoolsAuthorizePermissionsQuery$data, currentPoolId?: AgentPoolId) => currentPoolId,
  function reducePoolsAuthorizePermissionsResponse(
    {agentPools}: AgentsPagesPoolsAuthorizePermissionsQuery$data,
    currentPoolId?: AgentPoolId,
  ): ReadonlyArray<AgentPool> {
    if (agentPools == null) {
      return emptyArray
    }

    const stringCurrentPoolId = stringifyId(currentPoolId)
    let currentPoolNode = null
    const allowedPools = [] as Array<AgentPool>

    for (let i = 0; i < agentPools.edges.length; i++) {
      const node = agentPools.edges[i].node
      if (node.rawId === stringCurrentPoolId) {
        currentPoolNode = node

        if (node.permissions?.authorizeAgents) {
          // For the pool agent belongs to, it's enough to have `authorizeAgents` permission.
          allowedPools.push({id: toAgentPoolId(node.rawId), name: node.name} as AgentPool)
        }
      } else if (node.permissions?.manageAgents && node.permissions.authorizeAgents) {
        // For the pools the agent does not belong to, both `manageAgents` and `authorizeAgents` permissions are requires.
        // This is because before we authorize it, it has to be moved to the target pool.
        allowedPools.push({id: toAgentPoolId(node.rawId), name: node.name} as AgentPool)
      }
    }

    if (currentPoolId === DEFAULT_AGENT_POOL_ID || currentPoolNode?.permissions?.manageAgents) {
      return allowedPools
    }

    if (currentPoolNode?.permissions?.authorizeAgents) {
      return [{id: toAgentPoolId(currentPoolNode.rawId), name: currentPoolNode.name} as AgentPool]
    }

    return emptyArray
  },
)

export function useCanAuthorizeInPools(
  queryRef: PreloadedQuery<AgentsPagesPoolsAuthorizePermissionsQuery>,
  currentPoolId?: AgentPoolId,
): {isAgentLeft: boolean; canAuthorizeInPools: ReadonlyArray<AgentPool>} {
  const {isAgentLeft} = restApi.endpoints.getLicensingData.useQuery(
    {},
    {
      selectFromResult: ({data}) => {
        const {agentsLeft} = data ?? {}
        return {isAgentLeft: agentsLeft === -1 || (agentsLeft != null && agentsLeft > 0)}
      },
    },
  )
  const poolsAuthorizePermissionsResponse: AgentsPagesPoolsAuthorizePermissionsQuery$data =
    usePreloadedQuery(poolsAuthorizePermissionsQuery, queryRef)

  const canAuthorizeInPools = reducePoolsAuthorizePermissionsResponse(
    poolsAuthorizePermissionsResponse,
    currentPoolId,
  )

  return {isAgentLeft, canAuthorizeInPools}
}
